import '@mantine/carousel/styles.css'
import { Carousel } from '@mantine/carousel'
import { EmblaCarouselType } from 'embla-carousel-react'
import { MultiSlideCarouselProps } from './multi-slide-carousel.types'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getItemsSizes,
  useGetItemsToScroll,
  formatToCssWidthValue,
} from './multi-slide-carousel.utils'
import {
  NavArrow,
  NavArrowsWrapper,
  NavbarWrapper as HeaderWrapper,
  Title,
} from '../carousel/carousel'
import { Icon } from '../icon'

export function MultiSlideCarousel({
  title,
  description,
  items,
  itemsPerPage = { xs: 1 },
  slideGap = '0.5rem',
  scrollByPage = true,
  loop = false,
  titleClassName,
  headerClassName,
  navWrapperClassName,
}: Readonly<MultiSlideCarouselProps>) {
  const emblaRef = useRef<null | EmblaCarouselType>(null)
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(false)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false)

  const itemsToScroll = useGetItemsToScroll(itemsPerPage)

  const handlePrevious = () => {
    emblaRef.current?.scrollPrev()
  }

  const handleNext = () => {
    emblaRef.current?.scrollNext()
  }

  const updateButtonStates = useCallback(
    (embla: EmblaCarouselType | null) => {
      if (!embla || loop) {
        setPrevBtnDisabled(false)
        setNextBtnDisabled(false)
        return
      }

      setPrevBtnDisabled(!embla.canScrollPrev())
      setNextBtnDisabled(!embla.canScrollNext())
    },
    [loop]
  )

  useEffect(() => {
    if (emblaRef.current) {
      setTimeout(() => {
        updateButtonStates(emblaRef.current)
      }, 500)
    }
  })

  if (!items.length) {
    return <div />
  }

  return (
    <div>
      <HeaderWrapper className={headerClassName}>
        <Title className={titleClassName}>{title}</Title>
        <div className={navWrapperClassName}>
          {description || null}
          {items.length > 1 && items.length > itemsToScroll && (
            <NavArrowsWrapper role="navigation">
              <NavArrow
                onClick={handlePrevious}
                disabled={prevBtnDisabled}
                data-testid="carousel-nav-prev-btn"
              >
                <Icon name="arrow-left" size={5} />
              </NavArrow>
              <NavArrow
                onClick={handleNext}
                disabled={nextBtnDisabled}
                data-testid="carousel-nav-next-btn"
              >
                <Icon name="arrow-right" size={5} />
              </NavArrow>
            </NavArrowsWrapper>
          )}
        </div>
      </HeaderWrapper>
      <Carousel
        getEmblaApi={(embla) => {
          emblaRef.current = embla
          updateButtonStates(embla)
        }}
        align="start"
        withControls={false}
        slideSize={getItemsSizes(itemsPerPage)}
        slideGap={slideGap}
        loop={loop}
        containScroll="trimSnaps"
        slidesToScroll={scrollByPage ? itemsToScroll : 1}
      >
        {items.map(({ id, content }) => (
          <Carousel.Slide
            key={id}
            style={{
              maxWidth: formatToCssWidthValue(100 / itemsToScroll),
              minWidth: formatToCssWidthValue(100 / itemsToScroll),
            }}
          >
            {content}
          </Carousel.Slide>
        ))}
      </Carousel>
    </div>
  )
}
